'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { usePalette } from '@/contexts/PaletteContext'
import { palettes } from '@/lib/palettes'

export function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const { paletteName, setPalette, paletteNames } = usePalette()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
        scrolled ? 'glass border-border-subtle py-3' : 'border-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-baseline gap-0.5 group">
          <span
            className="font-display text-4xl font-light tracking-tight"
            style={{ color: scrolled ? 'var(--text-primary)' : 'var(--accent-cream)' }}
          >
            salle
          </span>
          <span
            className="text-xl font-medium"
            style={{ color: scrolled ? 'var(--text-tertiary)' : 'rgba(255,255,255,0.6)' }}
          >
            .art
          </span>
        </Link>

        {/* Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { label: 'Alerts', href: '/#alerts' },
            { label: 'Market', href: '/#market' },
            { label: 'Pricing', href: '/#pricing' },
            { label: 'About', href: '/about' },
          ].map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className="text-sm font-medium transition-colors hover:opacity-80"
              style={{ color: scrolled ? 'var(--text-secondary)' : 'rgba(255,255,255,0.8)' }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* CTA + Sign in + Palette Switcher */}
        <div className="flex items-center gap-2">
          <Link
            href="/get-started"
            className="pill text-sm px-4 py-2"
            style={{
              background: 'var(--accent-primary)',
              color: 'white',
            }}
          >
            Get started
          </Link>

          <Link
            href="/app/market"
            className="text-sm font-medium transition-colors hover:opacity-80 px-3 py-2"
            style={{ color: scrolled ? 'var(--text-secondary)' : 'rgba(255,255,255,0.8)' }}
          >
            Sign in
          </Link>

          {/* Divider */}
          <div
            className="w-px h-6 mx-1"
            style={{ background: scrolled ? 'var(--border-standard)' : 'rgba(255,255,255,0.2)' }}
          />

          {/* Palette Switcher */}
          <div className="relative">
            <button
              onClick={() => setPaletteOpen(!paletteOpen)}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105"
              style={{
                background: scrolled ? 'var(--accent-primary)' : 'rgba(255,255,255,0.15)',
                border: scrolled ? 'none' : '1px solid rgba(255,255,255,0.2)',
              }}
              title="Switch palette"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke={scrolled ? 'white' : 'var(--accent-cream)'}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                />
              </svg>
            </button>

            {paletteOpen && (
              <div className="absolute top-14 right-0 w-72 bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
                <div className="p-4 border-b border-border-subtle">
                  <h3 className="font-medium text-sm text-text-primary">Rothko Palettes</h3>
                  <p className="text-text-tertiary text-xs mt-1">
                    Click to switch the color scheme
                  </p>
                </div>
                <div className="p-2 max-h-80 overflow-y-auto">
                  {paletteNames.map((name) => {
                    const palette = palettes[name]
                    const isActive = name === paletteName

                    return (
                      <button
                        key={name}
                        onClick={() => {
                          setPalette(name)
                          setPaletteOpen(false)
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                          isActive ? 'bg-page' : 'hover:bg-page/50'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-border-subtle">
                          <div
                            className="w-full h-1/2"
                            style={{ background: palette.hero.topField[1] }}
                          />
                          <div
                            className="w-full h-1/2"
                            style={{ background: palette.hero.bottomField[0] }}
                          />
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}>
                            {palette.name}
                          </p>
                          <p className="text-xs text-text-tertiary truncate">
                            {palette.painting}
                          </p>
                        </div>
                        {isActive && (
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ background: 'var(--accent-primary)' }}
                          />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
